import React, { Suspense, useState, useEffect, useCallback, useRef } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { requestsApi, templatesApi, emailsApi } from '../services/api'
import { formatDate, formatDateTime, formatRecent, formatTime, parseUTC } from '../utils/dateUtils'
import { useAuth } from '../context/AuthContext'
import { isReadOnlyUser } from '../utils/readerAccess'
import UserAvatar from '../components/UserAvatar'
import DatePicker from '../components/DatePicker'
import SearchableSelect from '../components/SearchableSelect'
import ViewModeToggle from '../components/collection/ViewModeToggle'
import CollectionGrid from '../components/collection/CollectionGrid'
import MetaChip from '../components/collection/MetaChip'
import { trackSpotlight, useCardLink, useRowGlow } from '../components/collection/cardInteractions'
import useStoredViewMode from '../hooks/useStoredViewMode'
import { nsEntityDisplayName, nsEntityShortLabel } from '../utils/nsEntityLabels'
import { OpCoChip } from '../components/opco/OpCoBadge'
import { useOpCo } from '../context/OpCoContext'
import runCurtainTransition from '../utils/curtainTransition'
import {
  Plus,
  Filter,
  ChevronLeft,
  ChevronRight,
  FileText,
  FileStack,
  RefreshCw,
  AlertCircle,
  Check,
  ChevronDown,
  X,
  Search,
  ArrowRight,
  Building2,
  Paperclip,
  UserCheck,
  CheckCircle2,
  CalendarDays,
  CircleDot,
} from 'lucide-react'

// The New Request wizard (and the date-picker library its details step uses,
// ~180 KB) is only needed once someone starts a request, so it loads on demand:
// in the background once the list is up, and straight away on hover/focus of
// New Request. Once loaded it renders directly, without a Suspense flash.
let NewRequestModalLoaded = null
const loadNewRequestModal = () => import('../components/NewRequestStepOneModal').then((m) => {
  NewRequestModalLoaded = m.default
  return m
})
const NewRequestModalLazy = React.lazy(loadNewRequestModal)

function NewRequestModal(props) {
  if (NewRequestModalLoaded) {
    const Loaded = NewRequestModalLoaded
    return <Loaded {...props} />
  }
  return (
    <Suspense fallback={<NewRequestModalSkeleton />}>
      <NewRequestModalLazy {...props} />
    </Suspense>
  )
}

// Same frame as the wizard's full-page panel, shown only if it opens before
// its code has arrived.
function NewRequestModalSkeleton() {
  return (
    <div className="flex h-[calc(100vh-8rem)] w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900" aria-busy="true">
      <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-5 dark:border-gray-800">
        <div className="skeleton h-10 w-10 rounded-xl" />
        <div>
          <div className="skeleton h-7 w-48" />
          <div className="skeleton mt-2 h-4 w-80" />
        </div>
      </div>
      <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-3 dark:border-gray-800">
        {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-8 w-36 rounded-full" />)}
      </div>
      <div className="space-y-3 px-6 pt-5">
        <div className="skeleton h-3 w-20" />
        <div className="skeleton h-7 w-56" />
        <div className="skeleton h-11 w-full rounded-lg" />
        <div className="skeleton h-48 w-full rounded-xl" />
      </div>
    </div>
  )
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', dot: 'bg-yellow-500' },
  { value: 'processing', label: 'AI Extraction in Progress', dot: 'bg-blue-500' },
  { value: 'extracted', label: 'Extracted', dot: 'bg-indigo-500' },
  { value: 'reviewing', label: 'Editing Extracted Data', dot: 'bg-orange-500' },
  { value: 'pending_approval', label: 'Waiting for Review', dot: 'bg-purple-500' },
  { value: 'in_review', label: 'In Review', dot: 'bg-blue-500' },
  { value: 'approved', label: 'Request Submitted', dot: 'bg-green-500' },
  { value: 'rejected', label: 'Rejected', dot: 'bg-red-500' },
  { value: 'failed', label: 'Submission Failed', dot: 'bg-red-500' },
  { value: 'completed', label: 'Submission Successful', dot: 'bg-green-500' },
  { value: 'cancelled', label: 'Cancelled', dot: 'bg-gray-500' },
]

// Shared status helpers (module-level so both list rows and cards can use them)
const STATUS_CONFIG = {
  pending: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', dot: 'bg-yellow-500', label: 'Pending' },
  processing: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-300', dot: 'bg-blue-500', label: 'AI Extraction in Progress' },
  extracted: { bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-600 dark:text-indigo-300', dot: 'bg-indigo-500', label: 'Extracted' },
  reviewing: { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-600 dark:text-orange-300', dot: 'bg-orange-500', label: 'Editing Extracted Data' },
  pending_approval: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-300', dot: 'bg-purple-500', label: 'Waiting for Review' },
  in_review: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-300', dot: 'bg-blue-500', label: 'In Review' },
  approved: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-300', dot: 'bg-green-500', label: 'Request Submitted' },
  rejected: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-300', dot: 'bg-red-500', label: 'Rejected' },
  failed: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-300', dot: 'bg-red-500', label: 'Submission Failed' },
  completed: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-300', dot: 'bg-green-500', label: 'Submission Successful' },
  cancelled: { bg: 'bg-gray-50 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-300', dot: 'bg-gray-500', label: 'Cancelled' },
}
function getStatusBadge(status) {
  return STATUS_CONFIG[status] || STATUS_CONFIG.pending
}
// Solid soft tiles (no see-through tints), one per status family.
const STATUS_ICON_CLASS = {
  pending: 'text-amber-700 bg-amber-100 dark:text-amber-300 dark:bg-amber-950',
  processing: 'text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-950',
  extracted: 'text-indigo-700 bg-indigo-100 dark:text-indigo-300 dark:bg-indigo-950',
  reviewing: 'text-orange-700 bg-orange-100 dark:text-orange-300 dark:bg-orange-950',
  pending_approval: 'text-purple-700 bg-purple-100 dark:text-purple-300 dark:bg-purple-950',
  in_review: 'text-blue-700 bg-blue-100 dark:text-blue-300 dark:bg-blue-950',
  approved: 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-950',
  rejected: 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950',
  failed: 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950',
  completed: 'text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-950',
  cancelled: 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-800',
}
function getIconByStatus(status) {
  return STATUS_ICON_CLASS[status] || 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-800'
}
function confidenceBarClass(confidence) {
  return confidence >= 90 ? 'bg-green-500' : confidence >= 70 ? 'bg-yellow-500' : confidence > 0 ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'
}

// Filter bar styling, shared by every control so they line up and react alike.
const FILTER_LABEL = 'mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400'
const FILTER_BOX = 'h-10 rounded-lg border border-gray-200 bg-white text-sm shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-[border-color,box-shadow] duration-200 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
const FILTER_FOCUS = 'focus:outline-none focus:border-navy-500 focus:ring-4 focus:ring-navy-500/15'

// Reusable multi-select dropdown component
function MultiSelectDropdown({ options, selected, onChange, label, placeholder = 'Select...' }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const clearAll = (e) => {
    e.stopPropagation()
    onChange([])
  }

  const selectedLabels = options.filter(o => selected.includes(o.value)).map(o => o.label)

  return (
    <div ref={ref} className="relative">
      {label && <label className={FILTER_LABEL}>{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={`${FILTER_BOX} ${FILTER_FOCUS} flex w-full min-w-[170px] items-center justify-between gap-2 px-3 text-left ${open ? 'border-navy-500 ring-4 ring-navy-500/15' : ''}`}
      >
        <CircleDot className={`h-4 w-4 flex-shrink-0 ${selected.length ? 'text-navy-600 dark:text-navy-300' : 'text-gray-400'}`} />
        <span className={`truncate flex-1 ${selected.length ? 'font-medium text-gray-800 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
          {selected.length === 0
            ? placeholder
            : selected.length <= 2
              ? selectedLabels.join(', ')
              : `${selected.length} selected`}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {selected.length > 0 && (
            <span
              onClick={clearAll}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors cursor-pointer"
            >
              <X className="h-3 w-3 text-gray-400" />
            </span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-2 min-w-[240px] w-max max-w-[340px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden">
          {/* Select All / Clear All */}
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Filter status</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onChange(options.map(o => o.value))}
                className="text-xs text-navy-600 dark:text-navy-400 hover:underline font-medium"
              >
                All
              </button>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
              >
                Clear
              </button>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto p-1.5 space-y-0.5">
            {options.map((option) => {
              const isSelected = selected.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleOption(option.value)}
                  className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm text-left transition-colors ${
                    isSelected ? 'bg-navy-50 dark:bg-navy-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <span className={`flex items-center justify-center w-4 h-4 rounded border flex-shrink-0 transition-colors ${
                    isSelected
                      ? 'bg-navy-800 border-navy-800 text-white'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  {option.dot && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${option.dot}`} />}
                  <span className="text-gray-700 dark:text-gray-200 truncate">{option.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// Searchable single-select dropdown (combobox) — used for the Template filter
function Requests() {
  const { user: authUser } = useAuth()
  const isReader = isReadOnlyUser(authUser)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [requests, setRequests] = useState([])
  const [totalResults, setTotalResults] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateStepOneModal, setShowCreateStepOneModal] = useState(searchParams.get('create') === '1')
  const [creatingRequest, setCreatingRequest] = useState(false)
  const [selectedTemplateForCreate, setSelectedTemplateForCreate] = useState('')

  // Seed initial filters from URL params (e.g. when navigating from Dashboard trend)
  const _initTemplateId = searchParams.get('template_id') || ''

  // "Applied" filters - these are what the API is currently filtered by
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    statuses: [],
    dateFrom: '',
    dateTo: '',
    templateId: _initTemplateId,
  })

  // "Draft" filters - local input state, committed on Apply
  const [draftSearch, setDraftSearch] = useState('')
  const [draftStatuses, setDraftStatuses] = useState([])
  const [draftDateFrom, setDraftDateFrom] = useState('')
  const [draftDateTo, setDraftDateTo] = useState('')
  const [draftTemplateId, setDraftTemplateId] = useState(_initTemplateId)

  // Template options for filter dropdown
  const [templateCatalog, setTemplateCatalog] = useState([])
  const [templateOptions, setTemplateOptions] = useState([])
  const [loadingTemplateCatalog, setLoadingTemplateCatalog] = useState(false)
  const templateCatalogLoadedRef = useRef(false)

  // Keep create-flow screen in sync with URL so breadcrumb navigation works.
  useEffect(() => {
    setShowCreateStepOneModal(searchParams.get('create') === '1')
  }, [searchParams])

  // Pagination
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'))
  const [perPage, setPerPage] = useState(12)

  // View mode (list | grid), remembered per browser
  const [viewMode, setViewMode] = useStoredViewMode('requests')

  // Sorting
  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Track whether drafts differ from applied
  const hasUnappliedChanges =
    draftSearch !== appliedFilters.search ||
    JSON.stringify(draftStatuses) !== JSON.stringify(appliedFilters.statuses) ||
    draftDateFrom !== appliedFilters.dateFrom ||
    draftDateTo !== appliedFilters.dateTo ||
    draftTemplateId !== appliedFilters.templateId

  const hasActiveFilters =
    appliedFilters.search ||
    appliedFilters.statuses.length > 0 ||
    appliedFilters.dateFrom ||
    appliedFilters.dateTo ||
    appliedFilters.templateId

  // The filter dropdown needs nothing but id and name, so this fetch omits
  // include_fields. Asking for every template's full field list here made the
  // heaviest call in the app run on every Requests page load, for data only the
  // create-request modal reads.
  useEffect(() => {
    const fetchTemplateOptions = async () => {
      try {
        const res = await templatesApi.list()
        const templates = res.data?.templates || res.data || []
        setTemplateOptions(templates.map(t => ({ value: String(t.id), label: t.name })))
      } catch (_err) {
        setTemplateOptions([])
      }
    }

    fetchTemplateOptions()
  }, [])

  // NewRequestStepOneModal previews each template's fields and field_count,
  // which only come back with include_fields. Load that heavier payload when the
  // create flow actually opens; the modal already renders a loading state.
  useEffect(() => {
    if (!showCreateStepOneModal || templateCatalogLoadedRef.current) return
    const fetchTemplateCatalog = async () => {
      try {
        setLoadingTemplateCatalog(true)
        const res = await templatesApi.list({ include_fields: true })
        setTemplateCatalog(res.data?.templates || res.data || [])
        templateCatalogLoadedRef.current = true
      } catch (_err) {
        setTemplateCatalog([])
      } finally {
        setLoadingTemplateCatalog(false)
      }
    }

    fetchTemplateCatalog()
  }, [showCreateStepOneModal])

  // Fetch the wizard's code in the background once the list has shown, so
  // New Request opens without waiting on the network.
  useEffect(() => {
    if (isReader || loading || NewRequestModalLoaded) return undefined
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500))
    const cancel = window.cancelIdleCallback || clearTimeout
    const id = idle(() => { loadNewRequestModal().catch(() => {}) })
    return () => cancel(id)
  }, [isReader, loading])

  // New Request opens the create page behind a curtain wipe, and its close
  // button plays it in reverse (utils/curtainTransition). Leaving after a
  // submit navigates on, so that exit stays instant.
  const openCreateFlow = () => {
    runCurtainTransition({
      label: 'New request',
      apply: () => {
        setSelectedTemplateForCreate('')
        setShowCreateStepOneModal(true)
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set('create', '1')
        setSearchParams(nextParams, { replace: true })
      },
    })
  }

  const closeCreateFlow = () => {
    setShowCreateStepOneModal(false)
    setSelectedTemplateForCreate('')
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('create')
    setSearchParams(nextParams, { replace: true })
  }

  const leaveCreateFlow = () => {
    runCurtainTransition({ back: true, label: 'Requests', apply: closeCreateFlow })
  }

  const submitCreateFlow = async (payload) => {
    const templateId = payload?.templateId
    if (!templateId) return

    setSelectedTemplateForCreate(String(templateId))
    const requestDetails = payload?.requestDetails || {}
    const attachments = payload?.attachments || []

    try {
      setCreatingRequest(true)
      const res = await emailsApi.ingest({
        subject: requestDetails.subject || '',
        sender: requestDetails.sender || '',
        recipients: requestDetails.recipients || '',
        body: requestDetails.body || '',
        received_at: requestDetails.received_at || '',
        template_id: String(templateId),
        attachments,
        auto_analyze: true,
      })

      const createdRequestId =
        res?.data?.request_id ||
        res?.request_id ||
        null

      closeCreateFlow()
      if (createdRequestId) {
        navigate(`/requests/${createdRequestId}?mode=edit`)
      } else {
        fetchRequests(appliedFilters, currentPage, perPage)
      }
      return { ok: true }
    } catch (err) {
      return {
        ok: false,
        error: err?.response?.data?.error || 'Failed to start extraction',
      }
    } finally {
      setCreatingRequest(false)
    }
  }

  const fetchRequests = useCallback(async (filters, page, pageSize) => {
    try {
      setLoading(true)
      const res = await requestsApi.list({
        page,
        per_page: pageSize,
        status: filters.statuses.length > 0 ? filters.statuses.join(',') : undefined,
        search: filters.search || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        template_id: filters.templateId || undefined,
      })
      setRequests(res.data.requests || [])
      setTotalResults(res.data.pagination?.total || 0)
      setError(null)
    } catch (err) {
      console.error('Error fetching requests:', err)
      setError('Failed to load requests')
    } finally {
      setLoading(false)
    }
  }, [])

  // Single source of truth for request list loading
  useEffect(() => {
    fetchRequests(appliedFilters, currentPage, perPage)
  }, [appliedFilters, currentPage, perPage, fetchRequests])

  // Sync page to URL
  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('page', currentPage.toString())
    setSearchParams(nextParams, { replace: true })
  }, [currentPage])

  const handleApplyFilters = () => {
    const newFilters = {
      search: draftSearch,
      statuses: [...draftStatuses],
      dateFrom: draftDateFrom,
      dateTo: draftDateTo,
      templateId: draftTemplateId,
    }
    setAppliedFilters(newFilters)
    setCurrentPage(1)
  }

  const handleResetFilters = () => {
    setDraftSearch('')
    setDraftStatuses([])
    setDraftDateFrom('')
    setDraftDateTo('')
    setDraftTemplateId('')
    const emptyFilters = { search: '', statuses: [], dateFrom: '', dateTo: '', templateId: '' }
    setAppliedFilters(emptyFilters)
    setCurrentPage(1)
  }

  const totalPages = Math.ceil(totalResults / perPage)

  const sortedRequests = React.useMemo(() => {
    if (!sortField) return requests
    return [...requests].sort((a, b) => {
      let aVal, bVal
      switch (sortField) {
        case 'name':
          aVal = (a.title || a.name || '').toLowerCase(); bVal = (b.title || b.name || '').toLowerCase(); break
        case 'id':
          aVal = a.id || 0; bVal = b.id || 0; break
        case 'created':
          aVal = (parseUTC(a.created_at) || new Date(0)).getTime(); bVal = (parseUTC(b.created_at) || new Date(0)).getTime(); break
        case 'status':
          aVal = (a.status || '').toLowerCase(); bVal = (b.status || '').toLowerCase(); break
        case 'confidence':
          aVal = a.ai_confidence || 0; bVal = b.ai_confidence || 0; break
        default: return 0
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [requests, sortField, sortDirection])

  if (showCreateStepOneModal) {
    return (
      <div className="space-y-6">
        <NewRequestModal
          fullPage
          onClose={leaveCreateFlow}
          onSubmit={submitCreateFlow}
          templates={templateCatalog}
          loadingTemplates={loadingTemplateCatalog}
          selectedTemplateId={selectedTemplateForCreate}
          onSelectTemplate={setSelectedTemplateForCreate}
          submitting={creatingRequest}
        />
      </div>
    )
  }

  // First load: the same frame as the loaded page (header, filter bar, list or
  // grid) with placeholder blocks where the content will be, so nothing moves
  // when the data arrives.
  if (loading && requests.length === 0) {
    return <RequestsPageSkeleton viewMode={viewMode} perPage={perPage} />
  }

  if (error && requests.length === 0) {
    return (
      <div className="max-w-md mx-auto mt-12">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/40 rounded-full">
              <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800 dark:text-red-300">Failed to load requests</h3>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
              <button
                onClick={() => fetchRequests(appliedFilters, currentPage, perPage)}
                disabled={loading}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Requests</h1>
              {!loading && (
                <span className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold text-navy-700 ring-1 ring-inset ring-navy-200 dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
                  {totalResults}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage and track all document processing requests.</p>
          </div>
        </div>
        {!isReader && (
          <button
            onClick={openCreateFlow}
            onPointerEnter={loadNewRequestModal}
            onFocus={loadNewRequestModal}
            className="cta-new relative overflow-hidden inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-navy-800 text-white text-sm font-semibold shadow-sm ring-1 ring-navy-900/20 hover:bg-navy-900 hover:shadow-md"
          >
            <span aria-hidden="true" className="cta-new__sheen" />
            <Plus className="cta-new__icon h-4 w-4" />
            New Request
          </button>
        )}
      </div>

      {/* Requests Table with Filters */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-clip">
        {/* Filter Header */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">All Requests</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{totalResults} request{totalResults !== 1 ? 's' : ''} found</p>
            </div>
            {/* View toggle */}
            <ViewModeToggle value={viewMode} onChange={setViewMode} className="flex-shrink-0" />
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-[300px]">
              <label htmlFor="requests-search" className={FILTER_LABEL}>Search</label>
              <div className="group/search relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-colors group-focus-within/search:text-navy-600 dark:group-focus-within/search:text-navy-300" />
                <input
                  id="requests-search"
                  type="text"
                  value={draftSearch}
                  onChange={(e) => setDraftSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  placeholder="Title, description, documents…"
                  className={`${FILTER_BOX} ${FILTER_FOCUS} w-full pl-9 pr-3 text-gray-800 placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500`}
                />
              </div>
            </div>
            <div>
              <span className={FILTER_LABEL}>Date Range</span>
              {/* One framed control for both ends of the range. */}
              <div className={`${FILTER_BOX} flex items-center gap-1 pl-3 pr-1 focus-within:border-navy-500 focus-within:ring-4 focus-within:ring-navy-500/15`}>
                <CalendarDays className={`mr-0.5 h-4 w-4 flex-shrink-0 ${draftDateFrom || draftDateTo ? 'text-navy-600 dark:text-navy-300' : 'text-gray-400'}`} />
                <DatePicker
                  value={draftDateFrom}
                  onChange={setDraftDateFrom}
                  max={draftDateTo || undefined}
                  placeholder="From"
                  ariaLabel="From date"
                  openOnClick
                  className="filter-date h-8 w-[6.75rem] cursor-pointer rounded-md bg-transparent px-1.5 text-sm tabular-nums text-gray-800 transition-colors hover:bg-gray-50 focus:outline-none focus:bg-navy-50 dark:text-gray-100 dark:hover:bg-gray-700/50 dark:focus:bg-gray-700/70"
                />
                <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600" />
                <DatePicker
                  value={draftDateTo}
                  onChange={setDraftDateTo}
                  min={draftDateFrom || undefined}
                  placeholder="To"
                  ariaLabel="To date"
                  openOnClick
                  className="filter-date h-8 w-[6.75rem] cursor-pointer rounded-md bg-transparent px-1.5 text-sm tabular-nums text-gray-800 transition-colors hover:bg-gray-50 focus:outline-none focus:bg-navy-50 dark:text-gray-100 dark:hover:bg-gray-700/50 dark:focus:bg-gray-700/70"
                />
                {draftDateFrom || draftDateTo ? (
                  <button
                    type="button"
                    onClick={() => { setDraftDateFrom(''); setDraftDateTo('') }}
                    className="ml-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                    title="Clear dates"
                    aria-label="Clear dates"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <span className="w-1" aria-hidden="true" />
                )}
              </div>
            </div>
            <MultiSelectDropdown
              label="Status"
              options={STATUS_OPTIONS}
              selected={draftStatuses}
              onChange={setDraftStatuses}
              placeholder="All Status"
            />
            <div className="min-w-[180px]">
              <span className={FILTER_LABEL}>Template</span>
              <SearchableSelect
                value={draftTemplateId}
                onChange={setDraftTemplateId}
                options={templateOptions}
                allLabel="All Templates"
                placeholder="All Templates"
                searchPlaceholder="Search templates…"
                triggerClassName="!h-10 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-[border-color,box-shadow] duration-200 hover:border-gray-300 focus:!ring-4 focus:!ring-navy-500/15 dark:hover:border-gray-600"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleApplyFilters}
                disabled={loading || !hasUnappliedChanges}
                className={`inline-flex h-10 items-center gap-2 rounded-lg px-5 text-sm font-semibold transition-all duration-200 disabled:cursor-not-allowed ${
                  hasUnappliedChanges
                    ? 'bg-navy-800 text-white shadow-sm ring-1 ring-navy-900/20 hover:bg-navy-900 hover:shadow-md enabled:hover:-translate-y-0.5 enabled:active:translate-y-0 enabled:active:scale-[0.98] disabled:opacity-60'
                    : 'border border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-500'
                }`}
              >
                <Filter className="h-4 w-4" />
                Apply
                {hasUnappliedChanges && (
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-white/70 motion-safe:animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                  </span>
                )}
              </button>
              {hasActiveFilters && (
                <button
                  onClick={handleResetFilters}
                  disabled={loading}
                  className="inline-flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  title="Reset all filters"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Active filter tags */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <span className="text-xs text-gray-500 dark:text-gray-400">Active filters:</span>
              {appliedFilters.search && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300 rounded text-xs">
                  Search: "{appliedFilters.search}"
                </span>
              )}
              {appliedFilters.statuses.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300 rounded text-xs">
                  Status: {appliedFilters.statuses.length <= 3
                    ? appliedFilters.statuses.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')
                    : `${appliedFilters.statuses.length} selected`}
                </span>
              )}
              {(appliedFilters.dateFrom || appliedFilters.dateTo) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300 rounded text-xs">
                  Date: {appliedFilters.dateFrom || '...'} → {appliedFilters.dateTo || '...'}
                </span>
              )}
              {appliedFilters.templateId && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300 rounded text-xs">
                  Template: {templateOptions.find(t => t.value === appliedFilters.templateId)?.label || appliedFilters.templateId}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Refetching over existing results: a sliver of progress, and the
            grid dims (CollectionGrid) instead of blanking. */}
        <div className="relative h-0.5 overflow-hidden" aria-hidden="true">
          {loading && requests.length > 0 && (
            <span className="collection-progress-bar absolute inset-y-0 left-0 w-1/3 rounded-full bg-navy-500/80 dark:bg-navy-400/80" />
          )}
        </div>

        {/* Body: empty state / list / grid */}
        <div key={viewMode} className="animate-view-in">
        {requests.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-navy-50 to-navy-50 dark:from-navy-900/40 dark:to-navy-900/20 ring-1 ring-navy-100 dark:ring-navy-800">
              <FileStack className="h-8 w-8 text-navy-500 dark:text-navy-300" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-100">No requests found</h3>
            <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
              {hasActiveFilters ? 'Try adjusting your filters, or create a new request.' : 'New requests will appear here once documents are processed.'}
            </p>
          </div>
        ) : viewMode === 'list' ? (
          <RequestListView
            requests={sortedRequests}
            busy={loading}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        ) : (
          // Keyed on which requests are shown, not their order: a new page or
          // filter replays the entrance, while re-sorting glides cards into place.
          <CollectionGrid
            key={requests.map(r => r.id).sort().join('-')}
            items={sortedRequests}
            getKey={r => r.id}
            busy={loading}
            className={`p-4 grid gap-4 ${REQUEST_GRID_COLUMNS}`}
          >
            {(req) => <RequestCard request={req} />}
          </CollectionGrid>
        )}
        </div>

        {/* Pagination */}
        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span>
              {totalResults === 0
                ? 'No results'
                : `Showing ${(currentPage - 1) * perPage + 1}\u2013${Math.min(currentPage * perPage, totalResults)} of ${totalResults} result${totalResults !== 1 ? 's' : ''}`}
            </span>
            <span className="border-l border-gray-200 dark:border-gray-700 h-4" />
            <span className="flex items-center gap-1.5">
              Rows per page
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="px-2 py-1 border border-gray-200 dark:border-gray-700 rounded text-sm dark:bg-gray-800 dark:text-gray-200"
              >
                <option value={12}>12</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            {generatePageNumbers(currentPage, totalPages).map((pg, i) => (
              <React.Fragment key={i}>
                {pg === '...' ? (
                  <span className="px-2 text-gray-400 dark:text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => setCurrentPage(pg)}
                    className={`w-8 h-8 rounded text-sm font-medium ${
                      currentPage === pg
                        ? 'bg-navy-800 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {pg}
                  </button>
                )}
              </React.Fragment>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

    </div>
  )
}

// Sort Icon Component
function SortIcon({ active, direction }) {
  return (
    <svg className={`h-3 w-3 transition-colors ${active ? 'text-navy-600 dark:text-navy-400' : 'text-gray-400'}`} viewBox="0 0 10 14" fill="currentColor">
      <path d="M5 0L9.33 5H0.67L5 0Z" className={active && direction === 'asc' ? 'opacity-100' : 'opacity-30'} />
      <path d="M5 14L0.67 9H9.33L5 14Z" className={active && direction === 'desc' ? 'opacity-100' : 'opacity-30'} />
    </svg>
  )
}

// ── List view ────────────────────────────────────────────────────────────────

// Shared by the list header and rows so the columns always line up. Spare
// width is shared out across the columns (the fr weights) instead of all going
// to the name, so wide screens don't leave a gap after it. Every column is
// always shown; below the width they need, the list scrolls
// sideways (as the table did). The OpCo column only exists in an OpCo
// admin's "All OpCos" view, like before.
const REQUEST_LIST_COLS = 'grid items-center gap-x-4 grid-cols-[minmax(14rem,2.6fr)_minmax(5.5rem,0.6fr)_minmax(6.5rem,0.8fr)_minmax(11rem,1.1fr)_minmax(7rem,0.8fr)_minmax(9rem,1fr)_6rem]'
const REQUEST_LIST_COLS_OPCO = 'grid items-center gap-x-4 grid-cols-[minmax(14rem,2.6fr)_minmax(5.5rem,0.6fr)_minmax(6.5rem,0.8fr)_minmax(11rem,1.1fr)_minmax(7rem,0.8fr)_minmax(9rem,1fr)_minmax(5.5rem,0.6fr)_6rem]'

const LIST_HEADER_CELL = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400 whitespace-nowrap'

function SortHeader({ field, label, sortField, sortDirection, onSort }) {
  const active = sortField === field
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`${LIST_HEADER_CELL} inline-flex items-center gap-1 justify-self-start rounded-sm transition-colors hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 dark:hover:text-gray-200 ${active ? 'text-gray-800 dark:text-gray-200' : ''}`}
    >
      {label}
      <SortIcon active={active} direction={active ? sortDirection : null} />
    </button>
  )
}

function RequestListView({ requests, busy, sortField, sortDirection, onSort }) {
  const { allOpcos } = useOpCo() || {}
  const cols = allOpcos ? REQUEST_LIST_COLS_OPCO : REQUEST_LIST_COLS
  const minWidth = allOpcos ? 'min-w-[70rem]' : 'min-w-[64rem]'
  const sortProps = { sortField, sortDirection, onSort }
  const headerTrackRef = useRef(null)

  // The header sits outside the sideways-scrolling rows so it can stick under
  // the top bar (a sticky element inside a scroll box only sticks to that
  // box). When the rows scroll sideways on narrow screens, the header follows.
  const syncHeader = (e) => {
    const track = headerTrackRef.current
    if (track) track.style.transform = `translate3d(${-e.currentTarget.scrollLeft}px, 0, 0)`
  }

  return (
    <div aria-busy={busy || undefined} className={`transition-opacity duration-300 ${busy ? 'pointer-events-none opacity-60' : ''}`}>
      <div className="sticky top-16 z-10 overflow-hidden border-b border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900">
        <div ref={headerTrackRef} className={`${minWidth} px-5 py-2.5 will-change-transform ${cols}`}>
          <SortHeader field="name" label="Request Name" {...sortProps} />
          <SortHeader field="id" label="Request" {...sortProps} />
          <SortHeader field="created" label="Created Date" {...sortProps} />
          <SortHeader field="status" label="Status" {...sortProps} />
          <SortHeader field="confidence" label="AI Confidence" {...sortProps} />
          <span className={LIST_HEADER_CELL}>Created By</span>
          {allOpcos && <span className={LIST_HEADER_CELL}>OpCo</span>}
          <span />
        </div>
      </div>
      <div className="overflow-x-auto" onScroll={syncHeader}>
        <div className={minWidth}>
          {/* Keyed on which requests are shown, not their order: a new page or
              filter replays the entrance, while re-sorting glides rows into place. */}
          <CollectionGrid
            key={requests.map(r => r.id).sort().join('-')}
            items={requests}
            getKey={r => r.id}
            enterFrom="translateY(10px)"
            className="tl-rows relative px-2 py-2 [&>div+div]:border-t [&>div+div]:border-gray-100 dark:[&>div+div]:border-gray-800/70"
          >
            {(req) => <RequestRow request={req} cols={cols} showOpco={!!allOpcos} />}
          </CollectionGrid>
        </div>
      </div>
    </div>
  )
}

// Memoised: a row only needs to re-render when its own request changes.
const RequestRow = React.memo(function RequestRow({ request, cols, showOpco }) {
  const navigate = useNavigate()
  const { user: _authUser } = useAuth()
  const isRowReader = isReadOnlyUser(_authUser)
  // AI Confidence - same formula as Dashboard InstrumentRow
  const confidence = request.ai_confidence != null ? Math.min(100, Math.max(0, request.ai_confidence)) : 0

  // Get created by info
  const createdByName = request.created_by_name || 'Unknown'
  const createdByInitials = createdByName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'UN'

  // Get documents info
  const documents = request.documents || []
  const documentCount = documents.length || request.document_count || 0
  const documentNames = documents.length > 0
    ? documents.map(d => d.filename || d.name || 'Unknown').join('\n')
    : (request.document_names || []).join('\n')

  // Truncate description
  const description = request.description || ''
  const maxDescLength = 50
  const truncatedDesc = description.length > maxDescLength
    ? description.substring(0, maxDescLength) + '...'
    : description
  const showTooltip = description.length > maxDescLength

  const statusConfig = getStatusBadge(request.status)

  const handleRowClick = (e) => {
    // Don't navigate if clicking a link or button
    if (e.target.closest('a') || e.target.closest('button')) return
    const mode = (request.status === 'completed' || request.status === 'cancelled' || isRowReader) ? 'view' : 'edit'
    navigate(`/requests/${request.id}?mode=${mode}`)
  }

  const { glowRef, ...glowHandlers } = useRowGlow()

  return (
    <article
      {...glowHandlers}
      onClick={handleRowClick}
      data-ready
      className={`tl-row group cursor-pointer rounded-xl px-3 py-3 ${cols}`}
    >
      <span aria-hidden="true" className="tl-row__bg">
        <span ref={glowRef} className="tl-row__glow" />
      </span>
      <span aria-hidden="true" className="tl-row__bar" />

      {/* Request Name */}
      <div className="min-w-0">
        <div className="flex items-center gap-3.5">
          <div
            className={`tl-row__icon w-10 h-10 rounded-xl flex items-center justify-center relative flex-shrink-0 cursor-default ${getIconByStatus(request.status)}`}
            title={documentCount > 0 ? documentNames : 'No documents'}
          >
            <FileText className="h-5 w-5" />
            {documentCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-navy-600 text-white text-[10px] font-semibold tabular-nums rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ring-2 ring-white dark:ring-gray-900">
                {documentCount}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <Link to={`/requests/${request.id}`} className="min-w-0 truncate rounded-sm text-[15px] font-semibold tracking-tight text-gray-900 transition-colors duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 group-hover:text-navy-700 dark:text-gray-50 dark:group-hover:text-white">
                {request.title || request.email_subject || `Request #${String(request.id).substring(0, 8)}`}
              </Link>
              {request.template_bc_enabled && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-navy-100 text-navy-700 dark:bg-navy-900/30 dark:text-navy-300 flex-shrink-0">
                  <Building2 className="h-2.5 w-2.5" /> BC
                </span>
              )}
              {request.template_ns_enabled && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-navy-100 text-navy-700 dark:bg-navy-900/30 dark:text-navy-300 flex-shrink-0">
                  {nsEntityShortLabel(request.template_ns_entity_type)}
                </span>
              )}
            </div>
            <p className={`mt-0.5 truncate text-[13px] ${description ? 'text-gray-500 dark:text-gray-400' : 'italic text-gray-400 dark:text-gray-500'}`} title={showTooltip ? description : undefined}>
              {description ? truncatedDesc : 'No description'}
            </p>
          </div>
        </div>
      </div>

      {/* Request */}
      <div>
        <span className="inline-flex rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">#{String(request.id).substring(0, 8)}</span>
      </div>

      {/* Created Date */}
      <div className="whitespace-nowrap">
        <p className="text-sm tabular-nums text-gray-700 dark:text-gray-300">{formatDate(request.created_at)}</p>
        <p className="mt-0.5 text-xs tabular-nums text-gray-400 dark:text-gray-500">{formatTime(request.created_at)}</p>
      </div>

      {/* Status */}
      <div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig.bg} ${statusConfig.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`}></span>
          {statusConfig.label}
        </span>
      </div>

      {/* AI Confidence */}
      <div>
        <div className="flex items-center gap-2">
          <div className="w-14 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${confidenceBarClass(confidence)}`} style={{ width: `${confidence}%` }} />
          </div>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 tabular-nums w-9">{confidence}%</span>
        </div>
      </div>

      {/* Created By */}
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="tl-row__avatar inline-flex flex-shrink-0">
          <UserAvatar user={{ id: request.created_by, username: createdByName }} size="sm" className="!h-7 !w-7 !text-[11px] ring-2 ring-white dark:ring-gray-900" />
        </span>
        <span className="truncate text-sm font-medium text-gray-700 dark:text-gray-300" title={createdByName}>{createdByName}</span>
      </div>

      {/* OpCo */}
      {showOpco && (
        <div>
          <OpCoChip code={request?.opco_code} name={request?.opco_name} />
        </div>
      )}

      {/* Open / View */}
      <div className="flex justify-end">
        <span aria-hidden="true" className="tl-open">
          <span className="tl-open__bg" />
          <span className="tl-open__label">{(request.status === 'completed' || request.status === 'cancelled' || isRowReader) ? 'View' : 'Open'}</span>
          <ChevronRight className="tl-open__icon h-4 w-4" />
        </span>
      </div>
    </article>
  )
})

// ── Grid view ────────────────────────────────────────────────────────────────

// Fills the row at whatever width the sidebar leaves, instead of jumping
// between fixed breakpoint column counts.
const REQUEST_GRID_COLUMNS = 'grid-cols-[repeat(auto-fill,minmax(min(100%,18.5rem),1fr))]'

// Status → colour family for the cards. Labels stay in STATUS_CONFIG above.
const STATUS_TONE = {
  pending: 'amber',
  processing: 'sky',
  extracted: 'indigo',
  reviewing: 'orange',
  pending_approval: 'violet',
  in_review: 'sky',
  approved: 'emerald',
  rejected: 'rose',
  failed: 'rose',
  completed: 'emerald',
  cancelled: 'slate',
}

// Still moving through the pipeline, so the status dot pulses.
const LIVE_STATUSES = new Set(['pending', 'processing'])

// Literal class strings so Tailwind keeps them. `rgb` feeds the card's
// pointer spotlight (--card-glow).
const TONES = {
  amber: {
    rgb: '245 158 11',
    chip: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/20',
    dot: 'bg-amber-500',
    line: 'via-amber-500/70',
  },
  sky: {
    rgb: '14 165 233',
    chip: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/20',
    dot: 'bg-sky-500',
    line: 'via-sky-500/70',
  },
  indigo: {
    rgb: '99 102 241',
    chip: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20 dark:bg-indigo-400/10 dark:text-indigo-300 dark:ring-indigo-400/20',
    dot: 'bg-indigo-500',
    line: 'via-indigo-500/70',
  },
  orange: {
    rgb: '249 115 22',
    chip: 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-400/10 dark:text-orange-300 dark:ring-orange-400/20',
    dot: 'bg-orange-500',
    line: 'via-orange-500/70',
  },
  violet: {
    rgb: '139 92 246',
    chip: 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/20',
    dot: 'bg-violet-500',
    line: 'via-violet-500/70',
  },
  emerald: {
    rgb: '16 185 129',
    chip: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20',
    dot: 'bg-emerald-500',
    line: 'via-emerald-500/70',
  },
  rose: {
    rgb: '244 63 94',
    chip: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/20',
    dot: 'bg-rose-500',
    line: 'via-rose-500/70',
  },
  slate: {
    rgb: '100 116 139',
    chip: 'bg-gray-100 text-gray-600 ring-gray-500/20 dark:bg-gray-400/10 dark:text-gray-300 dark:ring-gray-400/20',
    dot: 'bg-gray-400',
    line: 'via-gray-400/70',
  },
}

const RING_R = 17
const RING_C = 2 * Math.PI * RING_R

// AI confidence as a ring that draws itself in. A request still extracting
// gets a spinning arc, and one with no score gets a dash, not a "0%" that
// reads like a terrible result.
function ConfidenceRing({ status, confidence }) {
  const working = status === 'processing'
  const scored = !working && confidence > 0
  const stroke = confidence >= 90 ? 'text-emerald-500' : confidence >= 70 ? 'text-amber-500' : 'text-rose-500'
  const label = working
    ? 'Extraction in progress'
    : scored ? `AI confidence ${confidence}%` : 'No confidence score yet'

  return (
    <div role="img" aria-label={label} title={label} className="relative h-11 w-11 flex-shrink-0">
      <svg viewBox="0 0 44 44" className="h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="22" cy="22" r={RING_R} fill="none" strokeWidth="3.5" className="stroke-gray-100 dark:stroke-gray-800" />
        {scored && (
          <circle
            cx="22" cy="22" r={RING_R} fill="none" strokeWidth="3.5" strokeLinecap="round" stroke="currentColor"
            strokeDasharray={`${RING_C}px`}
            className={`confidence-ring-arc ${stroke}`}
            style={{ '--ring-c': `${RING_C}px`, '--ring-offset': `${RING_C * (1 - confidence / 100)}px` }}
          />
        )}
        {working && (
          <circle
            cx="22" cy="22" r={RING_R} fill="none" strokeWidth="3.5" strokeLinecap="round" stroke="currentColor"
            strokeDasharray={`${RING_C * 0.28} ${RING_C}`}
            className="confidence-ring-spin text-sky-500"
          />
        )}
      </svg>
      <span aria-hidden="true" className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold tabular-nums text-gray-800 dark:text-gray-100">
        {scored ? (
          <>
            <span className="confidence-ring-num" style={{ '--ring-value': confidence }} />
            <span className="ml-px text-[8px] font-medium text-gray-400 dark:text-gray-500">%</span>
          </>
        ) : !working && (
          <span className="text-gray-400 dark:text-gray-500">–</span>
        )}
      </span>
    </div>
  )
}

function RequestCard({ request }) {
  const { user } = useAuth()
  const isReader = isReadOnlyUser(user)
  const status = request.status
  const statusConfig = getStatusBadge(status)
  const tone = TONES[STATUS_TONE[status]] || TONES.amber
  const live = LIVE_STATUSES.has(status)
  const confidence = request.ai_confidence != null ? Math.round(Math.min(100, Math.max(0, request.ai_confidence))) : 0
  const title = request.title || request.email_subject || `Request #${String(request.id).substring(0, 8)}`
  const description = request.description || ''
  const mode = (status === 'completed' || status === 'cancelled' || isReader) ? 'view' : 'edit'
  const href = `/requests/${request.id}?mode=${mode}`
  const cardLink = useCardLink(href)

  const createdByName = request.created_by_name || 'Unknown'
  const documentNames = request.document_names?.length
    ? request.document_names
    : (request.documents || []).map(d => d.filename || d.name).filter(Boolean)
  const documentCount = request.document_count || documentNames.length
  const reviewer = (status === 'pending_approval' || status === 'in_review') ? request.assigned_to_name : null
  // Why it stopped, where we know: a NetSuite rejection or a cancel note.
  const stopReason = (status === 'failed' || status === 'rejected')
    ? request.ns_post_error
    : status === 'cancelled' ? request.cancellation_reason : null

  return (
    <article
      {...cardLink}
      onPointerMove={trackSpotlight}
      style={{ '--card-glow': tone.rgb }}
      className="collection-card group relative flex h-full cursor-pointer flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-gray-300 hover:shadow-[0_18px_40px_-22px_rgba(16,24,40,0.35)] motion-safe:hover:-translate-y-1 motion-safe:active:translate-y-0 motion-safe:active:scale-[0.985] has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-navy-500/50 has-[a:focus-visible]:ring-offset-2 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700 dark:hover:shadow-[0_18px_40px_-22px_rgba(0,0,0,0.8)] dark:has-[a:focus-visible]:ring-offset-gray-950"
    >
      {/* Hairline in the status colour that draws out from the centre on hover */}
      <span aria-hidden="true" className={`pointer-events-none absolute inset-x-8 top-0 h-px origin-center scale-x-0 bg-gradient-to-r from-transparent ${tone.line} to-transparent transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100`} />

      <div className="flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex min-w-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${tone.chip}`}>
            <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
              {live && <span className={`absolute inset-0 rounded-full opacity-60 motion-safe:animate-ping ${tone.dot}`} />}
              <span className={`relative h-1.5 w-1.5 rounded-full ${tone.dot}`} />
            </span>
            <span className="truncate">{statusConfig.label}</span>
          </span>
          <span className="flex-shrink-0 font-mono text-[11px] text-gray-400 dark:text-gray-500">#{String(request.id).substring(0, 8)}</span>
        </div>

        <div className="mt-3 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[15px] font-semibold leading-6 tracking-tight text-gray-900 dark:text-gray-50">
              <Link to={href} className="rounded-sm transition-colors group-hover:text-navy-700 focus:outline-none dark:group-hover:text-white">
                {title}
              </Link>
            </h3>
            <p className="mt-0.5 line-clamp-2 min-h-[2.5rem] text-[13px] leading-5 text-gray-500 dark:text-gray-400" title={description || undefined}>
              {description || <span className="italic text-gray-400 dark:text-gray-500">No description</span>}
            </p>
          </div>
          <ConfidenceRing status={status} confidence={confidence} />
        </div>

        {stopReason && (
          <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-rose-50 px-2.5 py-1.5 text-xs leading-4 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
            <AlertCircle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            <span className="line-clamp-2" title={stopReason}>{stopReason}</span>
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {request.template_name && (
            <MetaChip icon={FileStack} title={`Template: ${request.template_name}`} className="max-w-[12rem]">
              {request.template_name}
            </MetaChip>
          )}
          {request.template_bc_enabled && <MetaChip icon={Building2} accent title="Business Central">BC</MetaChip>}
          {request.template_ns_enabled && (
            <MetaChip accent title={`NetSuite ${nsEntityDisplayName(request.template_ns_entity_type)}`}>
              {nsEntityShortLabel(request.template_ns_entity_type)}
            </MetaChip>
          )}
          {request.ns_po_number && (
            <MetaChip icon={CheckCircle2} accent title="Posted to NetSuite">{request.ns_po_number}</MetaChip>
          )}
          {documentCount > 0 && (
            <MetaChip icon={Paperclip} title={documentNames.join('\n') || undefined}>
              {documentCount} file{documentCount !== 1 ? 's' : ''}
            </MetaChip>
          )}
          {reviewer && <MetaChip icon={UserCheck} title={`Reviewer: ${reviewer}`}>{reviewer}</MetaChip>}
        </div>
      </div>

      <footer className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-3 dark:border-gray-800">
        <div className="flex min-w-0 items-center gap-2">
          <UserAvatar user={{ id: request.created_by, username: createdByName }} size="sm" className="!h-6 !w-6 !text-[10px]" />
          <span className="truncate text-xs font-medium text-gray-700 dark:text-gray-300" title={createdByName}>{createdByName}</span>
          <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">·</span>
          <time dateTime={request.created_at || undefined} title={formatDateTime(request.created_at)} className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
            {formatRecent(request.created_at)}
          </time>
        </div>
        <span aria-hidden="true" className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-semibold text-navy-600 opacity-0 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-safe:-translate-x-1 group-hover:translate-x-0 group-hover:opacity-100 group-has-[a:focus-visible]:opacity-100 dark:text-navy-300">
          {mode === 'view' ? 'View' : 'Open'} <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </footer>
    </article>
  )
}

function RequestsPageSkeleton({ viewMode, perPage }) {
  const rows = Math.min(perPage || 12, 8)
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading requests">
      {/* Page header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3.5">
          <div className="skeleton hidden h-11 w-11 rounded-xl sm:block" />
          <div>
            <div className="flex h-8 items-center gap-2.5">
              <div className="skeleton h-7 w-32" />
              <div className="skeleton h-5 w-9 rounded-full" />
            </div>
            <div className="mt-1 flex h-5 items-center"><div className="skeleton h-4 w-72" /></div>
          </div>
        </div>
        <div className="skeleton h-9 w-36 rounded-lg" />
      </div>

      <div className="overflow-clip rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        {/* Card header + filter bar */}
        <div className="border-b border-gray-100 px-5 py-4 dark:border-gray-800">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <div className="flex h-6 items-center"><div className="skeleton h-4 w-28" /></div>
              <div className="flex h-5 items-center"><div className="skeleton h-3.5 w-32" /></div>
            </div>
            <div className="skeleton h-9 w-[9.5rem] rounded-lg" />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {[
              ['w-14', 'w-full sm:w-[300px]'],
              ['w-20', 'w-[18rem]'],
              ['w-12', 'w-[170px]'],
              ['w-16', 'w-[180px]'],
            ].map(([label, box], i) => (
              <div key={i} className={box.startsWith('w-full') ? box : ''}>
                <div className="mb-1.5 flex h-4 items-center"><div className={`skeleton h-2.5 ${label}`} /></div>
                <div className={`skeleton h-10 rounded-lg ${box}`} />
              </div>
            ))}
            <div className="skeleton h-10 w-[6.5rem] rounded-lg" />
          </div>
        </div>
        <div className="h-0.5" />

        {viewMode === 'grid' ? (
          <div className={`grid gap-4 p-4 ${REQUEST_GRID_COLUMNS}`}>
            {[...Array(6)].map((_, i) => <RequestCardSkeleton key={i} />)}
          </div>
        ) : (
          <RequestListSkeleton rows={rows} />
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4 dark:border-gray-800">
          <div className="skeleton h-4 w-64" />
          <div className="flex items-center gap-1.5">
            <div className="skeleton h-8 w-20 rounded" />
            <div className="skeleton h-8 w-8 rounded" />
            <div className="skeleton h-8 w-8 rounded" />
            <div className="skeleton h-8 w-16 rounded" />
          </div>
        </div>
      </div>
    </div>
  )
}

// Same grid as RequestListView, so every placeholder sits in its column.
function RequestListSkeleton({ rows }) {
  const { allOpcos } = useOpCo() || {}
  const cols = allOpcos ? REQUEST_LIST_COLS_OPCO : REQUEST_LIST_COLS
  const headers = ['w-24', 'w-14', 'w-20', 'w-12', 'w-20', 'w-16', ...(allOpcos ? ['w-10'] : [])]
  return (
    <div className="overflow-x-auto">
      <div className={allOpcos ? 'min-w-[70rem]' : 'min-w-[64rem]'}>
        <div className={`border-b border-gray-100 bg-gray-50 px-5 py-2.5 dark:border-gray-800 dark:bg-gray-900 ${cols}`}>
          {headers.map((w, i) => <div key={i} className="flex h-[18px] items-center"><div className={`skeleton h-2.5 ${w}`} /></div>)}
          <span />
        </div>
        <div className="divide-y divide-gray-100 px-2 py-2 dark:divide-gray-800/70">
          {[...Array(rows)].map((_, i) => (
            <div key={i} className={`px-3 py-3 ${cols}`} style={{ opacity: 1 - i * 0.09 }}>
              {/* Request Name */}
              <div className="flex min-w-0 items-center gap-3.5">
                <div className="skeleton h-10 w-10 flex-shrink-0 rounded-xl" />
                <div className="min-w-0 flex-1">
                  <div className="flex h-[22.5px] items-center gap-2">
                    <div className="skeleton h-4 w-36" />
                    {i % 3 === 0 && <div className="skeleton h-4 w-14 rounded" />}
                  </div>
                  <div className="mt-0.5 flex h-[19.5px] items-center"><div className="skeleton h-3 w-48 max-w-full" /></div>
                </div>
              </div>
              {/* Request */}
              <div className="skeleton h-5 w-16 rounded-md" />
              {/* Created Date */}
              <div>
                <div className="flex h-5 items-center"><div className="skeleton h-3.5 w-20" /></div>
                <div className="mt-0.5 flex h-4 items-center"><div className="skeleton h-3 w-10" /></div>
              </div>
              {/* Status */}
              <div className="skeleton h-6 w-28 rounded-full" />
              {/* AI Confidence */}
              <div className="flex items-center gap-2">
                <div className="skeleton h-1.5 w-14 rounded-full" />
                <div className="skeleton h-4 w-8" />
              </div>
              {/* Created By */}
              <div className="flex items-center gap-2.5">
                <div className="skeleton h-7 w-7 flex-shrink-0 rounded-full" />
                <div className="skeleton h-4 w-24" />
              </div>
              {allOpcos && <div className="skeleton h-4 w-12 rounded" />}
              <span />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function RequestCardSkeleton() {
  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center justify-between">
        <div className="skeleton h-5 w-32 rounded-full" />
        <div className="skeleton h-3 w-10" />
      </div>
      <div className="mt-3 flex items-start gap-3">
        <div className="flex-1 space-y-2">
          <div className="skeleton h-4 w-2/3" />
          <div className="skeleton h-3 w-full" />
          <div className="skeleton h-3 w-4/5" />
        </div>
        <div className="skeleton h-11 w-11 rounded-full" />
      </div>
      <div className="mt-3 flex gap-1.5">
        <div className="skeleton h-5 w-24" />
        <div className="skeleton h-5 w-14" />
      </div>
      <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
        <div className="skeleton h-6 w-6 rounded-full" />
        <div className="skeleton h-3 w-36" />
      </div>
    </div>
  )
}

// Utility Functions (formatDate & formatTime imported from ../utils/dateUtils)

function generatePageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  if (current <= 3) {
    return [1, 2, 3, '...', total]
  }
  if (current >= total - 2) {
    return [1, '...', total - 2, total - 1, total]
  }
  return [1, '...', current - 1, current, current + 1, '...', total]
}

export default Requests
